import { useEffect, useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import classNames from "classnames";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";

import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { faLock } from "@fortawesome/free-solid-svg-icons/faLock";

import CustomCard from "components/CustomCard";
import { ALWAYS_ENABLE_PRICE_CHART } from "components/EntityAnalyticsWidget";
import { entityCompactUrl } from "components/EntityCard";
import { entitySmartScoreUrl } from "components/EntitySmartScore";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { baseFetcher } from "utils/baseFetcher";
import { getNA, millionize } from "utils/common";
import { dataCloudHost } from "utils/envVariables";

import styles from "./style.module.scss";

export const entityQuoteURL = (yahooTicker: string) =>
  `${dataCloudHost}/v1/quote/${yahooTicker}`;

export const entityAdtvURL = (entity: any) => {
  if (!entity.security && !entity.yahooTicker) return null;
  return `/api/v2/price-api/get-chart?ticker=${entity.security}&yahoo_ticker=${entity.yahooTicker}&interval=y1`;
};

export const entityAdtvExchangeURL = (chart: any, compact: any) => {
  if (!chart || !compact) {
    return null;
  }

  if (!chart?.adtv_in_usd && compact?.currency !== "USD") {
    return `/nextjs/api/proxy/exchange?currency=${compact?.currency}&amount=${chart?.adt_val}`;
  }

  return null;
};

export const currentStatsURL = (isin: string) =>
  `${dataCloudHost}/v4/financials/entities/${isin}/current-stats`;

const rationalize = (num: number) => {
  return num ? numeral(num).format("0.00") : getNA();
};

const EntityStatsCard = ({
  entity,
  widget,
  showTitle,
  onEmpty,
  full,
  columns,
  titleClassName,
}: any) => {
  const { mobile } = useMediaQuery();
  const { authenticated, account: loggedInAccount } = useAuthentication();
  const { toggleModal } = useSignInSignUp();

  const { data: adtvRawData } = useApi(
    authenticated ? entityAdtvURL(entity) : "",
    baseFetcher
  );

  const url =
    authenticated || ALWAYS_ENABLE_PRICE_CHART
      ? entityCompactUrl(entity.security)
      : "";
  const { data: compactData } = useApi(url, baseFetcher);

  const { data: adtvExchangeData } = useApi(
    authenticated ? entityAdtvExchangeURL(adtvRawData, compactData) : "",
    baseFetcher
  );

  const adtvData = useMemo(() => {
    return {
      ...adtvRawData,
      adt_val: adtvExchangeData?.amount || adtvRawData?.adt_val,
    };
  }, [adtvRawData, adtvExchangeData]);

  const { data: currentStats, isValidating } = useApi(
    authenticated || ALWAYS_ENABLE_PRICE_CHART
      ? currentStatsURL(entity?.isin)
      : "",
    baseFetcher
  );

  useEffect(() => {
    if (!isValidating && currentStats?.error) {
      onEmpty && onEmpty();
    }
  }, [currentStats, isValidating, onEmpty]);

  const { data: entitySmartScore } = useApi(
    entity.smartScore ? null : entitySmartScoreUrl(entity.slug),
    baseFetcher
  );
  const smartScore = entitySmartScore || entity.smartScore;

  const fairPriceValue = useMemo(() => {
    const price = compactData?.price;
    const divider = smartScore?.overall;

    const fairValue = price / divider - 1;

    if (fairValue <= -0.5) return { value: -0.5, percentage: 0 };
    if (fairValue >= 0.5) return { value: 0.5, percentage: 100 };

    return { value: fairValue, percentage: (fairValue - -0.5) * 100 };
  }, [compactData?.price, smartScore?.overall]);

  const fairValueLabel = useMemo(
    () => (
      <div className="flex items-center justify-center">
        <p className="mb-0">Price Gauge</p>
        <OverlayTrigger
          placement={mobile ? "bottom" : "top"}
          overlay={
            <Tooltip>
              <p>
                Smartkarma&apos;s Price Gauge is updated daily using a
                quantitative approach across 60,000 global equities. In the
                gauge, green (LHS) represents an attractively priced stock,
                while red (RHS) represents a richly priced stock
              </p>
            </Tooltip>
          }
        >
          <p className="mb-0 ml-[8px] cursor-pointer">
            <FontAwesomeIcon icon={faInfoCircle} className="text-[10px]" />
          </p>
        </OverlayTrigger>
      </div>
    ),
    [mobile]
  );

  const adtvLabel = useMemo(
    () => (
      <div className="flex items-center justify-center">
        <p className="mb-0">ADTV (USDm)</p>
        <OverlayTrigger
          placement={mobile ? "bottom" : "top"}
          overlay={
            <Tooltip>
              <p>60-day average daily traded value</p>
            </Tooltip>
          }
        >
          <p className="mb-0 ml-[8px] cursor-pointer">
            <FontAwesomeIcon icon={faInfoCircle} className="text-[10px]" />
          </p>
        </OverlayTrigger>
      </div>
    ),
    [mobile]
  );

  const advLabel = useMemo(
    () => (
      <div className="flex items-center justify-center">
        <p className="mb-0">ADV (millions)</p>
        <OverlayTrigger
          placement={mobile ? "bottom" : "top"}
          overlay={
            <Tooltip>
              <p>60-day average daily traded volume</p>
            </Tooltip>
          }
        >
          <p className="mb-0 ml-[8px] cursor-pointer">
            <FontAwesomeIcon icon={faInfoCircle} className="text-[10px]" />
          </p>
        </OverlayTrigger>
      </div>
    ),
    [mobile]
  );

  const fairValueComponent = useMemo(() => {
    const color =
      fairPriceValue.percentage <= 35
        ? "border-t-smart-teal"
        : fairPriceValue.percentage <= 65
        ? "border-t-yellow-300"
        : "border-t-bearish-red";

    if (!authenticated) {
      return (
        <div
          className="relative w-full h-full flex items-center justify-center cursor-pointer group"
          onClick={() => toggleModal("sign-in")}
        >
          <div className="absolute z-10 w-[100%] h-[100%] flex items-center justify-center">
            <FontAwesomeIcon
              icon={faLock}
              className="text-ink-pink text-[13px] group-hover:text-[15px] transition-all"
            />
          </div>
          <div className="relative blur-sm">
            <div className="h-[6px] rounded-full min-w-[150px] max-w-[200px] bg-gradient-to-r from-smart-teal via-yellow-300 via-[percentage:40%_60%] to-bearish-red"></div>
          </div>
        </div>
      );
    }

    return (
      <div className="relative">
        <div
          className={twMerge(
            "absolute top-[-10px] border-solid border-[8px] border-transparent",
            color
          )}
          style={{
            left: `${fairPriceValue.percentage}%`,
            transform: "translateX(-9px)",
          }}
        ></div>
        <div
          className={classNames(
            "h-[6px] rounded-full min-w-[150px] max-w-[200px] bg-gradient-to-r from-smart-teal via-yellow-300 via-[percentage:40%_60%] to-bearish-red",
            full && "min-w-[80px] md:min-w-[150px]"
          )}
        ></div>
      </div>
    );
  }, [fairPriceValue.percentage, authenticated, full, toggleModal]);

  const technicalStats: any[] = useMemo(() => {
    console.log(currentStats?.market_value_usd);

    if (full) {
      if (authenticated || ALWAYS_ENABLE_PRICE_CHART) {
        return [
          ...(loggedInAccount?.isCorporateOfficer
            ? []
            : [
                {
                  id: "fairValue",
                  isEmpty:
                    !fairPriceValue.value ||
                    !compactData?.price ||
                    !smartScore?.overall,
                  name: fairValueLabel,
                  text: fairValueComponent,
                },
              ]),
          {
            id: "adtv",
            name: adtvLabel,
            isEmpty: !adtvData?.adt_val,
            text: adtvData?.adt_val && millionize(adtvData?.adt_val),
          },
          {
            id: "adv",
            name: advLabel,
            isEmpty: !adtvData?.adt_vol,
            text: adtvData?.adt_vol && millionize(adtvData?.adt_vol),
          },
          {
            id: "52-week-range",
            name: "52-week Range",
            isEmpty: !compactData?.yearLow && !compactData?.yearHigh,
            text: `${compactData?.yearLow || "NA"} - ${
              compactData?.yearHigh || "NA"
            }`,
          },
          {
            id: "market-cap",
            name: "Market Cap (USDm)",
            isEmpty: !currentStats?.market_value_usd,
            text: numeral(currentStats?.market_value_usd).format("0,0"),
          },
          {
            id: "pe",
            name: "P/E",
            isEmpty: !currentStats?.pe,
            text:
              currentStats?.pe > 100 || currentStats?.pe < 0
                ? "N/M"
                : rationalize(currentStats?.pe),
          },
          {
            id: "pb",
            name: "P/B",
            isEmpty: !currentStats?.pb,
            text:
              currentStats?.pb > 100 || currentStats?.pb < 0
                ? "N/M"
                : rationalize(currentStats?.pb),
          },
          {
            id: "ev-ebitda",
            name: "EV/EBITDA",
            isEmpty: !currentStats?.ev_ebitda,
            text:
              currentStats?.ev_ebitda > 100 || currentStats?.ev_ebitda < 0
                ? "N/M"
                : rationalize(currentStats?.ev_ebitda),
          },
          {
            id: "ev-sales",
            name: "EV/Sales",
            isEmpty: !currentStats?.ev_sales,
            text:
              currentStats?.ev_sales > 100 || currentStats?.ev_sales < 0
                ? "N/M"
                : rationalize(currentStats?.ev_sales),
          },
          {
            id: "dividend-yield",
            name: "Dividend Yield (%)",
            isEmpty: !currentStats?.div_yld,
            text:
              currentStats?.div_yld > 100 || currentStats?.div_yld < 0
                ? "N/M"
                : numeral(currentStats?.div_yld).format("0.00"),
          },
        ];
      }
      return [
        ...(loggedInAccount?.isCorporateOfficer
          ? []
          : [
              {
                id: "fairValue",
                name: fairValueLabel,
                text: fairValueComponent,
              },
            ]),
        {
          id: "adtv",
          name: adtvLabel,
          text: "0.00",
        },
        {
          id: "adv",
          name: advLabel,
          text: "0.00",
        },
        {
          id: "52-week-range",
          name: "52-week Range",
          text: "0.00 - 0.00",
        },
        {
          id: "market-cap",
          name: "Market Cap (USDm)",
          text: "0.00",
        },
        {
          id: "pe",
          name: "P/E",
          text: "0.00",
        },
        {
          id: "pb",
          name: "P/B",
          text: "0.00",
        },
        {
          id: "ev-ebitda",
          name: "EV/EBITDA",
          text: "0.00",
        },
        {
          id: "ev-sales",
          name: "EV/Sales",
          text: "0.00",
        },
        {
          id: "dividend-yield",
          name: "Dividend (Yield)",
          text: "0.00",
        },
      ];
    }

    if (authenticated || ALWAYS_ENABLE_PRICE_CHART) {
      return [
        ...(loggedInAccount?.isCorporateOfficer
          ? []
          : [
              {
                id: "fairValue",
                isEmpty:
                  !fairPriceValue.value ||
                  !compactData?.price ||
                  !smartScore?.overall,
                name: fairValueLabel,
                text: fairValueComponent,
              },
            ]),
        {
          id: "52-week-range",
          name: "52-week Range",
          isEmpty: !compactData?.yearLow && !compactData?.yearHigh,
          text: `${compactData?.yearLow || "NA"} - ${
            compactData?.yearHigh || "NA"
          }`,
        },
        {
          id: "market-cap",
          name: "Market Cap (USDm)",
          isEmpty: !currentStats?.market_value_usd,
          text: numeral(currentStats?.market_value_usd).format("0,0"),
        },
        {
          id: "pe",
          name: "P/E",
          isEmpty: !currentStats?.pe,
          text:
            currentStats?.pe > 100 || currentStats?.pe < 0
              ? "N/M"
              : rationalize(currentStats?.pe),
        },
        {
          id: "dividend-yield",
          name: "Dividend Yield (%)",
          isEmpty: !currentStats?.div_yld,
          text:
            currentStats?.div_yld > 100 || currentStats?.div_yld < 0
              ? "N/M"
              : numeral(currentStats?.div_yld).format("0.00"),
        },
      ];
    }
    return [
      ...(loggedInAccount?.isCorporateOfficer
        ? []
        : [
            {
              id: "fairValue",
              name: fairValueLabel,
              text: fairValueComponent,
            },
          ]),
      {
        id: "52-week-range",
        name: "52-week Range",
        text: "0.00 - 0.00",
      },
      {
        id: "market-cap",
        name: "Market Cap (USDm)",
        text: "0.00",
      },
      {
        id: "pe",
        name: "P/E",
        text: "0.00",
      },
      {
        id: "dividend-yield",
        name: "Dividend (Yield)",
        text: "0.00",
      },
    ];
  }, [
    adtvData?.adt_val,
    adtvData?.adt_vol,
    adtvLabel,
    advLabel,
    authenticated,
    compactData?.price,
    compactData?.yearHigh,
    compactData?.yearLow,
    currentStats?.div_yld,
    currentStats?.ev_ebitda,
    currentStats?.ev_sales,
    currentStats?.market_value_usd,
    currentStats?.pb,
    currentStats?.pe,
    fairPriceValue.value,
    fairValueComponent,
    fairValueLabel,
    full,
    loggedInAccount?.isCorporateOfficer,
    smartScore?.overall,
  ]);

  if (mobile) {
    return (
      <div className={styles.mobileLayout}>
        {showTitle && (
          <h3 className={twMerge(styles.title, titleClassName)}>Statistics</h3>
        )}
        <div className={styles.statTable}>
          {technicalStats
            .filter((s) => !s.isEmpty)
            .slice(0, full ? Number.MAX_VALUE : 5)
            .map((stat: any) => {
              return (
                <div className={styles.item} key={stat.id}>
                  <div className={styles.name}>{stat.name}</div>
                  <div
                    className={classNames(
                      styles.value,
                      authenticated || ALWAYS_ENABLE_PRICE_CHART
                        ? ""
                        : "blur-sm"
                    )}
                  >
                    {stat.text}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
  }

  const stats = technicalStats.filter((s) => !s.isEmpty);

  if (!stats.length) return <></>;

  const content = (
    <>
      {showTitle && (
        <h3 className={twMerge(styles.title, titleClassName)}>Statistics</h3>
      )}
      <div className={styles.statTable}>
        {columns ? (
          <div className={twMerge(styles.mobileLayout, "mx-[-15px]")}>
            <div className={styles.statTable}>
              {technicalStats
                .filter((s) => !s.isEmpty)
                .map((stat: any) => {
                  return (
                    <div className={styles.item} key={stat.id}>
                      <div className={styles.name}>{stat.name}</div>
                      <div className={styles.value}>{stat.text}</div>
                    </div>
                  );
                })}
              {technicalStats.filter((s) => !s.isEmpty).length % 2 !== 0 && (
                <div className={styles.item} key={"odd"}></div>
              )}
            </div>
          </div>
        ) : (
          <table>
            <tbody>
              {stats.map((stat) => {
                return (
                  <tr key={stat.id}>
                    <td className="flex items-center">
                      <h4 className="text-[12px] mb-0 font-normal">
                        {stat.name}
                      </h4>
                    </td>
                    <td
                      className={twMerge(
                        authenticated || ALWAYS_ENABLE_PRICE_CHART
                          ? ""
                          : "blur-sm",
                        "flex items-center justify-center"
                      )}
                    >
                      {stat.text}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );

  if (widget) {
    return <CustomCard className={styles.card}>{content}</CustomCard>;
  }

  return content;
};

export default EntityStatsCard;
